import { FC, useCallback, useState } from "react";
import { useSelector } from "react-redux";

import { Checkbox, Text } from "@livechat/design-system-react-components";

import Block from "components/layout/Block";
import ConfirmActionModal from "components/modals/ConfirmActionModal";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { selectHasLiveChatIntegration } from "store/entities/integrations";

interface IProps {
  onSubmit: () => void;
  onConfirmationDisableToggle: (disabled: boolean) => void;
  onClose: () => void;
}

export const ConfirmContactFormChangesModal: FC<IProps> = ({ onSubmit, onConfirmationDisableToggle, onClose }) => {
  const [disableConfirmation, toggleDisableConfirmation] = useState(false);
  const hasLivechatIntegration = useSelector(selectHasLiveChatIntegration);

  const handleConfirm = useCallback(() => {
    if (disableConfirmation) {
      trackEngagement("Settings: Contact form confirmation after changes disabled", EventPlace.Settings);
      onConfirmationDisableToggle(true);
    }
    onSubmit();
    onClose();
  }, [disableConfirmation, onClose, onConfirmationDisableToggle, onSubmit]);

  const handleDisableConfirmationChange = useCallback(() => {
    toggleDisableConfirmation(!disableConfirmation);
  }, [disableConfirmation]);

  return (
    <ConfirmActionModal
      heading="Update contact form?"
      onClose={onClose}
      primaryAction={{
        label: "Update",
        dataTestId: "edit-contact-form-button",
        onClick: handleConfirm,
        actionType: "primary",
      }}
      secondaryAction={{
        label: "Back to editing",
        onClick: onClose,
      }}
    >
      <Block margin="0 auto">
        <Text>
          {`Saving this form will update the contact form currently published on your websites${hasLivechatIntegration ? " and in the LiveChat widget" : ""}.`}
        </Text>
        <Block marginTop="1em">
          <Checkbox id="disable-confirmation" onClick={handleDisableConfirmationChange} checked={disableConfirmation}>
            Don&lsquo;t show me this message again.
          </Checkbox>
        </Block>
      </Block>
    </ConfirmActionModal>
  );
};
